/**
 * Rejestr modeli jako DANE (docs/BILLING.md § Rejestr modeli jako dane).
 *
 * Deskryptory odzwierciedlaja `GET /api/v1/images/models` oraz
 * `GET /api/v1/images/models/{id}/endpoints` z OpenRoutera, zsynchronizowane
 * cronem do tabel `models` / `model_endpoints` i do kompaktowego snapshotu w KV
 * pod kluczem `registry:images:v1`.
 *
 * `router.ts` i `pricing.ts` czytaja WYLACZNIE z rejestru — nowy, tanszy model
 * staje sie dostepny przez wlaczenie wiersza, nie przez deploy.
 *
 * Zero DOM, zero Cloudflare — plik dziala w przegladarce (picker aspektu w
 * composerze) i w Workerze (pre-flight walidacja parametrow).
 */

import { z } from 'zod';
// Geometria (`'4:5'` -> 0.8, `'2K'` -> 2048 px) mieszka w `pricing.ts`, bo tam
// jest do niej tabela. Import jest jednokierunkowy: `pricing.ts` bierze stad
// WYLACZNIE typy (`import type`), wiec cyklu w runtime nie ma.
import { parseAspectRatio, resolutionLongEdgePx } from './pricing';

/** Klucz snapshotu w KV. */
export const MODEL_REGISTRY_KV_KEY = 'registry:images:v1';

/** Wersja formatu snapshotu — podbicie uniewaznia wpis w KV. */
export const MODEL_REGISTRY_SNAPSHOT_VERSION = 1;

/** `models.tier_hint` w D1. */
export const TIER_HINTS = ['sketch', 'preview', 'master', 'both'] as const;
export const TierHintSchema = z.enum(TIER_HINTS);
export type TierHint = (typeof TIER_HINTS)[number];

export const MODEL_MODALITIES = ['image'] as const;
export const ModelModalitySchema = z.enum(MODEL_MODALITIES);
export type ModelModality = (typeof MODEL_MODALITIES)[number];

// ─────────────────────────────────────────────────────────────────────────────
// Cennik
// ─────────────────────────────────────────────────────────────────────────────

export const PRICING_KINDS = ['per_image', 'per_megapixel', 'per_token'] as const;
export type PricingKind = (typeof PRICING_KINDS)[number];

/** Flat za obraz, niezaleznie od rozdzielczosci (np. seedream-4.5: $0.04). */
export type PerImagePricing = {
  readonly kind: 'per_image';
  readonly usdPerImage: number;
};

/** Za megapiksel wyjscia (np. flux.2-klein-4b: $0.014/MP). */
export type PerMegapixelPricing = {
  readonly kind: 'per_megapixel';
  readonly usdPerMegapixel: number;
};

/** Za tokeny (rodzina gemini image). */
export type PerTokenPricing = {
  readonly kind: 'per_token';
  readonly usdPerInputToken: number;
  readonly usdPerOutputToken: number;
  /** Osobna stawka za tokeny obrazowe, gdy provider ja rozdziela. */
  readonly usdPerImageToken?: number;
};

export type ModelPricing = PerImagePricing | PerMegapixelPricing | PerTokenPricing;

export const PerImagePricingSchema = z.strictObject({
  kind: z.literal('per_image'),
  usdPerImage: z.number().min(0),
});

export const PerMegapixelPricingSchema = z.strictObject({
  kind: z.literal('per_megapixel'),
  usdPerMegapixel: z.number().min(0),
});

export const PerTokenPricingSchema = z.strictObject({
  kind: z.literal('per_token'),
  usdPerInputToken: z.number().min(0),
  usdPerOutputToken: z.number().min(0),
  usdPerImageToken: z.number().min(0).optional(),
});

export const ModelPricingSchema = z.discriminatedUnion('kind', [
  PerImagePricingSchema,
  PerMegapixelPricingSchema,
  PerTokenPricingSchema,
]);

// ─────────────────────────────────────────────────────────────────────────────
// Capability
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Capability jednego modelu obrazowego.
 *
 * Nazwy pol sa nasze (camelCase); mapowanie z odpowiedzi OpenRoutera
 * (`input_references`, `aspect_ratios`, ...) robi `services/openrouter/registry.ts`.
 */
export type ModelCapability = {
  /** Pelne id providera, np. `bytedance-seed/seedream-4.5`. */
  readonly id: string;
  readonly name?: string;
  readonly modality: ModelModality;
  /**
   * Wspierane rozdzielczosci w zapisie API, np. `['1K','2K','4K']`.
   *
   * PUSTA TABLICA MA ZNACZENIE: model NIE PRZYJMUJE parametru `resolution`
   * (w `supported_parameters` OpenRoutera nie ma takiego klucza — tak jest np.
   * dla `black-forest-labs/flux.2-klein-4b`). Wyslanie go konczy sie 400,
   * wiec kompilator ma go wtedy POMINAC, a nie zgadywac wartosc.
   */
  readonly supportedResolutions: readonly string[];
  /** Wspierane proporcje, np. `['1:1','4:5','16:9']`. Pusta = brak parametru. */
  readonly supportedAspectRatios: readonly string[];
  /**
   * Wspierane `output_format` w KOLEJNOSCI PODANEJ PRZEZ DOSTAWCE (pierwszy =
   * preferowany przez niego). `undefined` znaczy "model nie przyjmuje tego
   * parametru" — seedream i rodzina gemini image nie maja go w
   * `supported_parameters` i format wyjscia wybiera dostawca.
   *
   * Rozroznienie `undefined` (brak parametru) od `[]` (parametr jest, ale bez
   * wartosci) jest celowe: pierwsze znaczy "nie wysylaj", drugie "nie da sie
   * spelnic" — oba konczy sie pominieciem pola, ale w logu widac roznice.
   */
  readonly supportedOutputFormats?: readonly string[];
  /** Maksymalne `n` w jednym wywolaniu (siatka 3x3 wymaga >= 9). */
  readonly maxN: number;
  /** Maksymalna liczba `input_references` — napedza drabine degradacji. */
  readonly maxRefs: number;
  readonly supportsSeed: boolean;
  readonly pricing: ModelPricing;
  readonly tierHint: TierHint;
  /** Parametry przekazywane do providera bez interpretacji. */
  readonly passthroughParams: readonly string[];
  /** `models.enabled` — wylaczenie wiersza usuwa model z routera. */
  readonly enabled: boolean;
  /** Provider endpointu, z ktorego pochodzi cennik. */
  readonly provider?: string;
  /** Najwyzsza wspierana rozdzielczosc (`models.max_resolution`). */
  readonly maxResolution?: string;
};

export const ModelCapabilitySchema = z.strictObject({
  id: z.string().min(1).max(200),
  name: z.string().max(200).optional(),
  modality: ModelModalitySchema.default('image'),
  supportedResolutions: z.array(z.string().min(1).max(16)).max(32),
  supportedAspectRatios: z.array(z.string().min(1).max(16)).max(32),
  supportedOutputFormats: z.array(z.string().min(1).max(16)).max(16).optional(),
  maxN: z.int().min(1).max(64),
  maxRefs: z.int().min(0).max(64),
  supportsSeed: z.boolean(),
  pricing: ModelPricingSchema,
  tierHint: TierHintSchema,
  passthroughParams: z.array(z.string().min(1).max(64)).max(64),
  enabled: z.boolean().default(true),
  provider: z.string().max(120).optional(),
  maxResolution: z.string().max(16).optional(),
});

/**
 * Snapshot rejestru trzymany w KV (`registry:images:v1`) i wstrzykiwany do
 * kompilacji promptu oraz do pickera aspektow w composerze.
 */
export type ModelRegistrySnapshot = {
  readonly version: number;
  /** Epoch ms ostatniej synchronizacji (cron co 6 h). */
  readonly syncedAt: number;
  /** Modele posortowane po `id` — determinizm snapshotu i jego hasha. */
  readonly models: readonly ModelCapability[];
};

export const ModelRegistrySnapshotSchema = z.strictObject({
  version: z.literal(MODEL_REGISTRY_SNAPSHOT_VERSION),
  syncedAt: z.int().min(0),
  models: z.array(ModelCapabilitySchema).max(500),
});

// ─────────────────────────────────────────────────────────────────────────────
// Pomocnicze (czyste, deterministyczne)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maksymalne `n`, ktore model NAPRAWDE honoruje.
 *
 * `supported_parameters` seedreama deklaruje `n: {min: 1, max: 10}`, ale
 * dostawca ten parametr IGNORUJE: zadanie `n=2` zwraca jeden obraz i kosztuje
 * jak jeden ($0.04) — zmierzone. Siatka 3x3 jednym wywolaniem jest wiec na tym
 * modelu niemozliwa, mimo ze rejestr twierdzi inaczej.
 *
 * Tak samo jak `MODEL_MIN_OUTPUT_PIXELS`, ta tablica MUSI zyc w kodzie:
 * pochodzi z pomiaru, a nie z API, wiec kazdy sync skasowalby ja z danych.
 * TODO(spec): zglosic rozjazd OpenRouterowi — jesli poprawia metadane albo
 * zachowanie, wpis znika.
 */
export const MODEL_EFFECTIVE_MAX_N: Readonly<Record<string, number>> = {
  'bytedance-seed/seedream-4.5': 1,
};

/** Capability z nalozonymi poprawkami, ktorych rejestr nie zna. */
function withMeasuredQuirks(model: ModelCapability): ModelCapability {
  const cap = MODEL_EFFECTIVE_MAX_N[model.id];
  if (cap === undefined || model.maxN <= cap) return model;
  return { ...model, maxN: cap };
}

/** Model po id albo `null`. */
export function findModel(snapshot: ModelRegistrySnapshot, id: string): ModelCapability | null {
  const found = snapshot.models.find((model) => model.id === id);
  return found === undefined ? null : withMeasuredQuirks(found);
}

/** Wlaczone modele, posortowane po `id` (stabilna kolejnosc dla routera). */
export function enabledModels(snapshot: ModelRegistrySnapshot): readonly ModelCapability[] {
  return snapshot.models
    .filter((model) => model.enabled)
    .map(withMeasuredQuirks)
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export function supportsResolution(capability: ModelCapability, resolution: string): boolean {
  return capability.supportedResolutions.includes(resolution);
}

export function supportsAspect(capability: ModelCapability, aspect: string): boolean {
  return capability.supportedAspectRatios.includes(aspect);
}

// ─────────────────────────────────────────────────────────────────────────────
// Ktore parametry wolno w ogole wyslac
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Czy model przyjmuje parametr `resolution`. Brak klucza w
 * `supported_parameters` = parametr nieobslugiwany; wyslanie go daje 400
 * (`black-forest-labs/flux.2-klein-4b`, zmierzone).
 */
export function acceptsResolutionParam(capability: ModelCapability): boolean {
  return capability.supportedResolutions.length > 0;
}

/** Czy model przyjmuje parametr `aspect_ratio`. */
export function acceptsAspectParam(capability: ModelCapability): boolean {
  return capability.supportedAspectRatios.length > 0;
}

/** Czy model przyjmuje parametr `output_format` (patrz `supportedOutputFormats`). */
export function acceptsOutputFormatParam(capability: ModelCapability): boolean {
  const formats = capability.supportedOutputFormats;
  return formats !== undefined && formats.length > 0;
}

export function supportsOutputFormat(capability: ModelCapability, format: string): boolean {
  return capability.supportedOutputFormats?.includes(format) ?? false;
}

/**
 * Wartosc `output_format` do wyslania albo `null` = "pomin pole".
 *
 * Deterministyczne: zyczenie wygrywa, gdy jest na liscie; inaczej schodzimy na
 * PIERWSZY format zadeklarowany przez dostawce (kolejnosc z `supported_parameters`
 * jest jego wlasna preferencja, dlatego jej nie sortujemy).
 */
export function resolveOutputFormatParam(
  capability: ModelCapability,
  wish: string,
): { readonly value: string | null; readonly reason: 'wish' | 'unsupported_param' | 'degraded' } {
  if (!acceptsOutputFormatParam(capability)) return { value: null, reason: 'unsupported_param' };
  if (supportsOutputFormat(capability, wish)) return { value: wish, reason: 'wish' };
  const first = capability.supportedOutputFormats?.[0];
  return first === undefined
    ? { value: null, reason: 'unsupported_param' }
    : { value: first, reason: 'degraded' };
}

/**
 * Przeciecie wspieranych proporcji kandydujacych modeli — picker aspektu w
 * composerze nie moze pozwolic na kompozycje, ktorej nie da sie wygenerowac
 * (docs/BILLING.md).
 */
export function intersectAspectRatios(capabilities: readonly ModelCapability[]): readonly string[] {
  if (capabilities.length === 0) return [];
  const first = capabilities[0];
  if (first === undefined) return [];
  let common = first.supportedAspectRatios.slice();
  for (const capability of capabilities.slice(1)) {
    common = common.filter((aspect) => capability.supportedAspectRatios.includes(aspect));
  }
  return common.slice().sort();
}

/**
 * Wartosc `resolution` do wyslania albo `null` = "pomin pole".
 *
 * Niewspierane zyczenie POMIJAMY, zamiast podstawiac najblizsza wartosc z enuma.
 * Powod jest empiryczny: enum nie jest pelnym opisem tego, co model przyjmie.
 * `bytedance-seed/seedream-4.5` deklaruje {1K, 2K, 4K}, ale ma dodatkowy,
 * nieudokumentowany prog 3 686 400 pikseli WYJSCIA — a liczba pikseli zalezy od
 * aspektu, ktorego enum nie wiaze z rozdzielczoscia. Stad `1K` przy `4:5` to
 * 820x1024 = 839 680 px i 400, mimo ze `1K` jest w enumie. Kazda nasza podmiana
 * jest wiec zgadywaniem obarczonym progiem, ktorego nie widzimy, podczas gdy
 * rozmiar domyslny dostawcy jest z definicji przez niego akceptowany.
 *
 * Wyjatkiem jest zyczenie, ktore model zna — to przechodzi bez zmian, wiec
 * `upscale_ai` i master 4K nadal sterują rozmiarem jawnie.
 */
export function resolveResolutionParam(
  capability: ModelCapability,
  wish: string,
  aspect?: string | null,
): {
  readonly value: string | null;
  readonly reason: 'wish' | 'unsupported_param' | 'dropped' | 'below_pixel_floor';
} {
  if (!acceptsResolutionParam(capability)) return { value: null, reason: 'unsupported_param' };
  if (!supportsResolution(capability, wish)) return { value: null, reason: 'dropped' };
  if (belowPixelFloor(capability.id, wish, aspect ?? null)) {
    return { value: null, reason: 'below_pixel_floor' };
  }
  return { value: wish, reason: 'wish' };
}

/**
 * Progi minimalnej liczby pikseli WYJSCIA, ktorych `supported_parameters` NIE
 * OPISUJE i ktore trzeba znac z pomiaru.
 *
 * `bytedance-seed/seedream-4.5` przyjmuje enum {1K, 2K, 4K}, ale odrzuca kazde
 * zadanie ponizej 3 686 400 pikseli wyjscia — a liczba pikseli zalezy od
 * PROPORCJI, ktorej enum z rozdzielczoscia nie wiaze. Zmierzone: `1K` @ 4:5 to
 * 820x1024 = 839 680 px -> 400; `2K` @ 4:5 to 1640x2048 = 3 358 720 px -> 400;
 * `2K` @ 1:1 -> 200; `4K` @ 4:5 -> 200.
 *
 * Ta tablica MUSI byc w kodzie, a nie w rejestrze: rejestr jest nadpisywany
 * synchronizacja z API, ktore o progu milczy. Zawezenie zapisane w
 * `fallbacks.ts` przetrwalo dokladnie do pierwszego udanego syncu.
 * TODO(spec): przeniesc do pola `models.min_output_pixels` w D1, gdy operator
 * bedzie mial gdzie je wpisac — dzis kazdy sync by je skasowal.
 */
export const MODEL_MIN_OUTPUT_PIXELS: Readonly<Record<string, number>> = {
  'bytedance-seed/seedream-4.5': 3_686_400,
};

/**
 * Czy `resolution` przy tym aspekcie zejdzie ponizej progu modelu.
 *
 * Bez znanego aspektu zakladamy NAJGORSZY przypadek z `CANVAS_ASPECTS` (9:16),
 * bo pomylka w te strone kosztuje pominiete pole, a w druga — blad 400.
 */
function belowPixelFloor(modelId: string, resolution: string, aspect: string | null): boolean {
  const floor = MODEL_MIN_OUTPUT_PIXELS[modelId];
  if (floor === undefined) return false;
  const longEdge = resolutionLongEdgePx(resolution);
  if (longEdge === null) return false;
  const ratio = aspect === null ? null : parseAspectRatio(aspect);
  // Krotsza krawedz = dluzsza * (min(w,h) / max(w,h)).
  const shortOverLong = ratio === null ? 9 / 16 : Math.min(ratio, 1 / ratio);
  return longEdge * longEdge * shortOverLong < floor;
}

/**
 * Wartosc `aspect_ratio` do wyslania albo `null` = "pomin pole".
 *
 * Zamiast odmawiac kompilacji, schodzimy na NAJBLIZSZY wspierany aspekt
 * (`4:5` -> `3:4` na kleinie, ktory `4:5` nie zna i odpowiada 400). Remisy
 * rozstrzyga porzadek leksykograficzny — wybor musi byc odtwarzalny.
 */
export function resolveAspectParam(
  capability: ModelCapability,
  wish: string,
): { readonly value: string | null; readonly reason: 'wish' | 'unsupported_param' | 'degraded' } {
  if (!acceptsAspectParam(capability)) return { value: null, reason: 'unsupported_param' };
  if (supportsAspect(capability, wish)) return { value: wish, reason: 'wish' };

  const wishRatio = parseAspectRatio(wish);
  if (wishRatio === null) return { value: null, reason: 'unsupported_param' };

  const scored = capability.supportedAspectRatios
    .slice()
    .map((token) => ({ token, ratio: parseAspectRatio(token) }))
    .filter((entry): entry is { token: string; ratio: number } => entry.ratio !== null)
    .map((entry) => ({ token: entry.token, distance: Math.abs(entry.ratio - wishRatio) }))
    .sort((a, b) => (a.distance !== b.distance ? a.distance - b.distance : a.token < b.token ? -1 : 1));
  const best = scored[0];
  return best === undefined
    ? { value: null, reason: 'unsupported_param' }
    : { value: best.token, reason: 'degraded' };
}

export type CapabilityRequest = {
  readonly n: number;
  readonly refCount: number;
  readonly resolution?: string;
  readonly aspect?: string;
  readonly needsSeed?: boolean;
};

/**
 * Powody, dla ktorych model NIE spelnia zapotrzebowania. Pusta lista = pasuje.
 * Kolejnosc powodow jest ustalona, zeby komunikaty bledow byly stabilne.
 */
export function capabilityShortfalls(
  capability: ModelCapability,
  request: CapabilityRequest,
): readonly string[] {
  const reasons: string[] = [];
  if (!capability.enabled) reasons.push('model disabled in registry');
  if (request.n > capability.maxN) reasons.push(`maxN ${String(capability.maxN)} < requested n ${String(request.n)}`);
  if (request.refCount > capability.maxRefs) {
    reasons.push(`maxRefs ${String(capability.maxRefs)} < requested refs ${String(request.refCount)}`);
  }
  // Model bez parametru `aspect_ratio` nie jest modelem, ktory "nie wspiera"
  // zadanej wartosci — on jej po prostu nie dostanie.
  //
  // `resolution` NIE JEST juz powodem dyskwalifikacji, i to jest celowe:
  // kompilator traktuje rozdzielczosc jako ZYCZENIE i pomija ja, gdy model jej
  // nie zna (`resolveResolutionParam`). Gdyby router nadal na niej odsiewal,
  // te dwie warstwy mowilyby co innego — zyczenie `1K` wyrzucaloby z podgladu
  // `seedream-4.5` (deklaruje tylko `4K`, bo nizsze tokeny lamie jego prog
  // pikselowy), mimo ze wywolanie bez tego pola dziala i jest tansze niz
  // ucieczka na inny model. Rozdzielczosc zostaje wiec wejsciem do WYCENY
  // (`DEFAULT_RESOLUTION_BY_TIER`), nie bramka doboru modelu.
  if (
    request.aspect !== undefined &&
    acceptsAspectParam(capability) &&
    !supportsAspect(capability, request.aspect)
  ) {
    reasons.push(`aspect "${request.aspect}" unsupported`);
  }
  if (request.needsSeed === true && !capability.supportsSeed) reasons.push('seed unsupported');
  return reasons;
}
